

import { Button, Alert, Combobox, FormField, TextInput, NumberInput, ToggleInput, useToast } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import type { SubscriptionPlanType, SubscriptionPlanView } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import { SUBSCRIPTION_PLAN_TYPE } from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useDialogDismiss } from "@/lib/hooks/useDialogDismiss";
import { useUpdateSubscriptionPlanMutation } from "../services/useUpdateSubscriptionPlanMutation";

type Props = {
  plan: SubscriptionPlanView;
};

export function SubscriptionPlanEditModal({ plan }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [planType, setPlanType] = useState<SubscriptionPlanType>(plan.type);
  const [amountValue, setAmountValue] = useState(String(plan.amount));
  const [lastPaidAmount, setLastPaidAmount] = useState(String(plan.amount));
  const isFreeTrial = planType === SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL;
  const { mutateAsync: updatePlan } = useUpdateSubscriptionPlanMutation();

  const resetFormState = () => {
    setPlanType(plan.type);
    const planAmount = String(plan.amount);
    setAmountValue(planAmount);
    setLastPaidAmount(planAmount);
    setError(null);
  };

  useDialogDismiss(isOpen, () => {
    resetFormState();
    setIsOpen(false);
  });

  const handleTypeChange = (nextType: SubscriptionPlanType) => {
    setPlanType(nextType);
    if (nextType === SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL) {
      if (amountValue.trim() && amountValue !== "0") {
        setLastPaidAmount(amountValue);
      }
      setAmountValue("0");
      return;
    }
    setAmountValue(lastPaidAmount);
  };

  const handleAmountChange = (value: string) => {
    setAmountValue(value);
    if (!isFreeTrial) {
      setLastPaidAmount(value);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    try {
      await updatePlan({
        subscriptionId: plan.id,
        nameAr: String(formData.get("nameAr") ?? ""),
        nameEn: String(formData.get("nameEn") ?? ""),
        type: planType,
        amount: Number(formData.get("amount")),
        periodDays: Number(formData.get("periodDays")),
        isActive: formData.get("isActive") === "on",
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
      return;
    }

    toast.success(t(keys.common.updateSuccess));
    setIsSubmitting(false);
    setIsOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline" className="h-9 rounded-full px-3 text-xs font-semibold transition max-lg:h-10"
        onClick={() => {
          resetFormState();
          setIsOpen(true);
        }}
      >
        {t(keys.common.edit)}
      </Button>
      {isOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            resetFormState();
            setIsOpen(false);
          }}
        >
          <div
            className="max-h-full w-full max-w-2xl overflow-y-auto rounded-2xl bg-[color:var(--surface)] p-6 shadow-[var(--shadow-md)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
                  {t(keys.admin.subscriptionEditTitle)}
                </h2>
                <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                  {t(keys.admin.subscriptionEditHint)}
                </p>
              </div>
              <Button variant="ghost"
                type="button"
                onClick={() => {
                  resetFormState();
                  setIsOpen(false);
                }}
                className="text-xs font-semibold text-[color:var(--text-muted)]"
              >
                {t(keys.common.cancel)}
              </Button>
            </div>
            <form className="mt-5 grid gap-4" onSubmit={handleSubmit}>
              {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
              <FormField label={t(keys.admin.subscriptionPlanNameAr)} name="nameAr" required>
                <TextInput
                  name="nameAr"
                  defaultValue={plan.nameAr}
                  required
                  placeholder={t(keys.placeholders.name)}
                />
              </FormField>
              <FormField label={t(keys.admin.subscriptionPlanNameEn)} name="nameEn">
                <TextInput name="nameEn" defaultValue={plan.nameEn ?? ""} />
              </FormField>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label={t(keys.admin.subscriptionPlanType)} name="type" required>
                  <Combobox
                    value={planType}
                    onChange={(value) => {
                      if (value)
                        handleTypeChange(value as SubscriptionPlanType);
                    }}
                    options={[
                      {
                        label: t(keys.admin.subscriptionPlanTypePaid),
                        value: SUBSCRIPTION_PLAN_TYPE.MONTHLY_SUBSCRIPTION,
                      },
                      {
                        label: t(keys.admin.subscriptionPlanTypeFreeTrial),
                        value: SUBSCRIPTION_PLAN_TYPE.FREE_TRIAL,
                      },
                    ]}
                    aria-label={t(keys.admin.subscriptionPlanType)}
                  />
                </FormField>
                <FormField
                  label={t(keys.admin.subscriptionPlanAmount)}
                  name="amount"
                  required
                  helperText={
                    isFreeTrial ? t(keys.admin.subscriptionPlanAmountFreeTrialHelper) : undefined
                  }
                >
                  <NumberInput
                    name="amount"
                    required
                    allowDecimal
                    value={amountValue}
                    onChange={(event) => handleAmountChange(event.target.value)}
                    disabled={isFreeTrial}
                  />
                </FormField>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  label={t(keys.admin.subscriptionPlanPeriodDays)}
                  name="periodDays"
                  required
                >
                  <NumberInput
                    name="periodDays"
                    defaultValue={String(plan.periodDays)}
                    required
                  />
                </FormField>
                <FormField label={t(keys.admin.subscriptionPlanIsActive)} name="isActive">
                  <ToggleInput name="isActive" defaultChecked={plan.isActive} />
                </FormField>
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="primary" className="h-10 rounded-lg px-4 text-sm font-semibold transition disabled:opacity-60"
              >
                {isSubmitting ? t(keys.common.loading) : t(keys.common.save)}
              </Button>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
